"use client";

import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  UpdateAiIntegrationData,
  UpdateAiIntegrationFormSchema,
} from "./schema";
import { useToast } from "@/hooks/use-toast";
import hooks from "@/hooks";
import { Form } from "@/components/ui/form";
import FormFields from "../Fields";
import Buttons from "@/components/core/Buttons";
import { IntegrationType } from "@/enum";

interface IUpdateAiIntegrationFormProps {
  ai_integration_id: string;
  name: string;
  integration_key: string;
  type: IntegrationType;
  onSuccess?: () => void;
}

const UpdateAiIntegrationForm = ({
  ai_integration_id,
  name,
  integration_key,
  type,
  onSuccess,
}: IUpdateAiIntegrationFormProps) => {
  const { toast } = useToast();

  const form = useForm<UpdateAiIntegrationData>({
    resolver: zodResolver(UpdateAiIntegrationFormSchema),
    defaultValues: {
      ai_integration_id,
      name,
      integration_key,
      type,
    },
  });

  const updateIntegrationService = hooks.useUpdateAiIntegration({
    onSuccess: () => {
      toast({
        title: "Integração atualizada com sucesso!",
        variant: "success",
      });
      if (onSuccess) onSuccess?.();
    },
    onError: (error: unknown) => {
      toast({
        title: "Não foi possível atualizar integração",
        description: (error as Error).message,
        variant: "warning",
      });
    },
  });

  async function onSubmit(data: UpdateAiIntegrationData) {
    await updateIntegrationService.mutateAsync(data);
  }

  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit(onSubmit)}
        className="w-full flex flex-col items-center gap-y-6"
      >
        <FormFields.InputFormField
          form={form}
          formName="name"
          placeholder="Insira o nome da integração"
          label="Nome da integração"
        />

        <FormFields.InputFormField
          form={form}
          formName="integration_key"
          label="Credencial"
          placeholder="Insira a credencial da integração"
          type="password"
        />

        <FormFields.SelectFormField
          form={form}
          formName="type"
          label="Tipo"
          placeholder="Selecione o tipo da integração"
          options={[
            { label: "OPEN_AI", value: "OPEN_AI" },
            { label: "TWILIO", value: "TWILIO" },
          ]}
        />

        <Buttons.DefaultButton
          className="py-6"
          type="submit"
          title="Salvar Alterações"
          isLoading={updateIntegrationService.isPending}
        />
      </form>
    </Form>
  );
};

export default UpdateAiIntegrationForm;
